import { useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { isUUID } from '@karehero/helpers/string';
import Loading from '@karehero/llama/components/pages/Loading/Loading';
import { ConversationalFormPage } from '@karehero/llama/pwa/pages/ConversationalFormPage/ConversationalFormPage';

import { DYNAMIC_ROUTES, ROUTES } from 'router/routes';
import {
  useGetByAccountQuery,
  useGetCareCircleQuery,
} from 'store/api/careCircle';
import { useBenefitsCalculatorChat } from 'hooks/useBenefitsCalculatorChat';
import { useCareCircle } from 'hooks/useCareCircle';
import { useGoBack } from 'hooks/useGoBack';

/** Said in each question when the care recipient's name is not known. */
const FALLBACK_CARE_RECIPIENT_NAME = 'your loved one';

/**
 * BenefitsEligibilityV2Form wires the v2 benefits calculator chat for the care
 * circle in the URL. Always shows the active care circle — redirects there when
 * the URL points elsewhere.
 */
export const BenefitsEligibilityV2Form = () => {
  const goBack = useGoBack();
  const { careCircleId: urlCareCircleId = '', chatSessionId = '' } =
    useParams();
  const { careCircleId: activeCareCircleId } = useCareCircle();
  // Loading the account's care circles sets the active one, if there is one.
  const { isSuccess: hasLoadedCareCircles, isError: hasCareCirclesFailed } =
    useGetByAccountQuery();
  const hasNoCareCircle =
    (hasLoadedCareCircles || hasCareCirclesFailed) && !activeCareCircleId;
  const isOtherCareCircle =
    !!activeCareCircleId && urlCareCircleId !== activeCareCircleId;
  // Nothing is loaded or started until the active circle is known and the URL
  // names it; any other circle in the URL is redirected away from.
  const careCircleId =
    !!activeCareCircleId && urlCareCircleId === activeCareCircleId
      ? urlCareCircleId
      : '';
  const { data: careCircle } = useGetCareCircleQuery(careCircleId, {
    skip: !careCircleId,
  });

  const {
    messages,
    currentStep,
    sessionID,
    isSessionReady,
    hasSessionFailed,
    isThinking,
    isSubmitting,
    answerWithOption,
    answerWithOptions,
  } = useBenefitsCalculatorChat({
    careRecipientName:
      careCircle?.careRecipientAccount?.firstName ||
      FALLBACK_CARE_RECIPIENT_NAME,
    careCircleID: careCircleId,
    chatSessionID: isUUID(chatSessionId) ? chatSessionId : undefined,
  });

  useEffect(() => {
    if (hasSessionFailed) goBack();
  }, [hasSessionFailed, goBack]);

  if (hasNoCareCircle) return <Navigate to={ROUTES.HOME} replace />;

  if (isOtherCareCircle) {
    return (
      <Navigate
        to={DYNAMIC_ROUTES.ELIGIBILITY_CALCULATOR_V2_FORM(activeCareCircleId)}
        replace
      />
    );
  }

  if (!isSessionReady) return <Loading />;

  // Pins the session picked up or started in the URL, so a reload comes back to it.
  if (sessionID && sessionID !== chatSessionId) {
    return (
      <Navigate
        to={DYNAMIC_ROUTES.ELIGIBILITY_CALCULATOR_V2_FORM(
          careCircleId,
          sessionID,
        )}
        replace
      />
    );
  }

  return (
    <ConversationalFormPage
      messages={messages}
      currentStep={currentStep}
      isThinking={isThinking}
      isSubmitting={isSubmitting}
      onBack={goBack}
      onChoose={answerWithOption}
      onChooseMany={answerWithOptions}
      // Every calculator question is a tap; nothing is typed.
      onSend={() => {}}
    />
  );
};

BenefitsEligibilityV2Form.displayName = 'BenefitsEligibilityV2Form';
export default BenefitsEligibilityV2Form;
